/**
 * Go Game Engine
 *
 * Core game logic: board setup, move validation, stone placement,
 * capture detection (flood-fill liberties), ko rule, pass/resign,
 * game-over detection, Chinese area scoring with komi, serialization.
 */

import {
  GoColor,
  GoBoard,
  GoCell,
  GoMove,
  GoMoveRecord,
  GoPosition,
  GoGameState,
  GoGameSettings,
  GoScore,
  GoTerritoryMap,
  GoBoardSize,
  DEFAULT_BOARD_SIZE,
  DEFAULT_KOMI,
  DIRECTIONS,
} from './types';

export class GoEngine {
  private state: GoGameState;

  constructor() {
    this.state = this.createEmptyState(DEFAULT_BOARD_SIZE);
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  /**
   * Initialize a new game with the given settings.
   */
  initializeGame(settings: GoGameSettings): void {
    const size = settings.boardSize || DEFAULT_BOARD_SIZE;
    const board = this.createEmptyBoard(size);

    this.state = {
      board,
      boardSize: size,
      currentPlayer: 'black',
      captures: { black: 0, white: 0 },
      moveHistory: [],
      koPoint: null,
      consecutivePasses: 0,
      gameStatus: 'active',
      komi: DEFAULT_KOMI,
      gameId: `go-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
    };
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  /**
   * Place a stone at (row, col) for the current player.
   * Validates the move, captures opponent groups with no liberties,
   * checks for ko, and advances the turn.
   * Returns the list of captured positions.
   */
  placeStone(row: number, col: number): GoPosition[] {
    if (this.state.gameStatus !== 'active') {
      throw new Error('Game is not active');
    }

    const { board, boardSize, currentPlayer, koPoint } = this.state;

    // Bounds check
    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) {
      throw new Error('Position out of bounds');
    }

    // Must be empty
    if (board[row][col] !== null) {
      throw new Error('Position is occupied');
    }

    // Ko check
    if (koPoint && koPoint.row === row && koPoint.col === col) {
      throw new Error('Ko rule violation: cannot recapture immediately');
    }

    // Tentatively place the stone
    const newBoard = this.cloneBoard(board);
    newBoard[row][col] = currentPlayer;

    // Check for captures of opponent groups
    const opponent = this.getOpponent(currentPlayer);
    const allCaptured: GoPosition[] = [];

    for (const [dr, dc] of DIRECTIONS) {
      const nr = row + dr;
      const nc = col + dc;
      if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && newBoard[nr][nc] === opponent) {
        const group = this.getGroup(newBoard, nr, nc, boardSize);
        const liberties = this.countGroupLiberties(newBoard, group, boardSize);
        if (liberties === 0) {
          // Capture this group
          for (const pos of group) {
            newBoard[pos.row][pos.col] = null;
            allCaptured.push(pos);
          }
        }
      }
    }

    // Check for suicide (self-capture without capturing anything)
    if (allCaptured.length === 0) {
      const selfGroup = this.getGroup(newBoard, row, col, boardSize);
      const selfLiberties = this.countGroupLiberties(newBoard, selfGroup, boardSize);
      if (selfLiberties === 0) {
        throw new Error('Suicide move: placing here would leave your group with no liberties');
      }
    }

    // Determine new ko point
    let newKoPoint: GoPosition | null = null;
    if (allCaptured.length === 1) {
      // Check if this is a single-stone capture that could create a ko
      const capturedPos = allCaptured[0];
      const placedGroup = this.getGroup(newBoard, row, col, boardSize);
      if (placedGroup.length === 1) {
        const placedLiberties = this.countGroupLiberties(newBoard, placedGroup, boardSize);
        if (placedLiberties === 1) {
          // The captured position becomes the ko point
          newKoPoint = capturedPos;
        }
      }
    }

    // Record the move
    const moveRecord: GoMoveRecord = {
      player: currentPlayer,
      move: { type: 'place', row, col },
      timestamp: Date.now(),
      captures: allCaptured,
    };

    // Update state
    this.state.board = newBoard;
    this.state.moveHistory.push(moveRecord);
    this.state.koPoint = newKoPoint;
    this.state.consecutivePasses = 0;

    // Update capture counts
    if (allCaptured.length > 0) {
      this.state.captures[currentPlayer] += allCaptured.length;
    }

    // Advance turn
    this.state.currentPlayer = opponent;

    return allCaptured;
  }

  /**
   * Current player passes their turn.
   */
  pass(): void {
    if (this.state.gameStatus !== 'active') {
      throw new Error('Game is not active');
    }

    const { currentPlayer } = this.state;
    const opponent = this.getOpponent(currentPlayer);

    const moveRecord: GoMoveRecord = {
      player: currentPlayer,
      move: { type: 'pass' },
      timestamp: Date.now(),
      captures: [],
    };

    this.state.moveHistory.push(moveRecord);
    this.state.consecutivePasses++;
    this.state.koPoint = null; // Ko is cleared on pass
    this.state.currentPlayer = opponent;

    // Check for game over (two consecutive passes)
    if (this.state.consecutivePasses >= 2) {
      this.endGame();
    }
  }

  // =========================================================================
  // Move Validation
  // =========================================================================

  /**
   * Get all valid moves for the current player.
   */
  getValidMoves(player?: GoColor): GoPosition[] {
    const p = player || this.state.currentPlayer;
    const { board, boardSize, koPoint } = this.state;
    const moves: GoPosition[] = [];

    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (board[row][col] !== null) continue;

        // Ko check
        if (koPoint && koPoint.row === row && koPoint.col === col) continue;

        // Try placing the stone
        const testBoard = this.cloneBoard(board);
        testBoard[row][col] = p;

        // Check for captures
        const opponent = this.getOpponent(p);
        let captured = false;
        for (const [dr, dc] of DIRECTIONS) {
          const nr = row + dr;
          const nc = col + dc;
          if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && testBoard[nr][nc] === opponent) {
            const group = this.getGroup(testBoard, nr, nc, boardSize);
            const liberties = this.countGroupLiberties(testBoard, group, boardSize);
            if (liberties === 0) {
              captured = true;
              for (const pos of group) {
                testBoard[pos.row][pos.col] = null;
              }
            }
          }
        }

        // Check for suicide
        if (!captured) {
          const selfGroup = this.getGroup(testBoard, row, col, boardSize);
          const selfLiberties = this.countGroupLiberties(testBoard, selfGroup, boardSize);
          if (selfLiberties === 0) continue; // Suicide — invalid
        }

        moves.push({ row, col });
      }
    }

    return moves;
  }

  /**
   * Check if a specific move is valid.
   */
  isValidMove(row: number, col: number, player?: GoColor): boolean {
    const p = player || this.state.currentPlayer;
    const { board, boardSize, koPoint } = this.state;

    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) return false;
    if (board[row][col] !== null) return false;
    if (koPoint && koPoint.row === row && koPoint.col === col) return false;

    // Try placing
    const testBoard = this.cloneBoard(board);
    testBoard[row][col] = p;

    const opponent = this.getOpponent(p);
    let captured = false;
    for (const [dr, dc] of DIRECTIONS) {
      const nr = row + dr;
      const nc = col + dc;
      if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && testBoard[nr][nc] === opponent) {
        const group = this.getGroup(testBoard, nr, nc, boardSize);
        const liberties = this.countGroupLiberties(testBoard, group, boardSize);
        if (liberties === 0) {
          captured = true;
          for (const pos of group) {
            testBoard[pos.row][pos.col] = null;
          }
        }
      }
    }

    if (!captured) {
      const selfGroup = this.getGroup(testBoard, row, col, boardSize);
      const selfLiberties = this.countGroupLiberties(testBoard, selfGroup, boardSize);
      if (selfLiberties === 0) return false;
    }

    return true;
  }

  // =========================================================================
  // Group & Liberty Analysis
  // =========================================================================

  /**
   * Get all stones in a connected group starting from (row, col).
   * Uses flood-fill.
   */
  getGroup(board: GoBoard, row: number, col: number, boardSize: number): GoPosition[] {
    const color = board[row][col];
    if (color === null) return [];

    const group: GoPosition[] = [];
    const visited = new Set<string>();
    const stack: GoPosition[] = [{ row, col }];

    while (stack.length > 0) {
      const pos = stack.pop()!;
      const key = `${pos.row},${pos.col}`;
      if (visited.has(key)) continue;
      visited.add(key);

      if (
        pos.row < 0 || pos.row >= boardSize ||
        pos.col < 0 || pos.col >= boardSize
      ) continue;

      if (board[pos.row][pos.col] !== color) continue;

      group.push(pos);

      for (const [dr, dc] of DIRECTIONS) {
        stack.push({ row: pos.row + dr, col: pos.col + dc });
      }
    }

    return group;
  }

  /**
   * Count the number of liberties (adjacent empty points) for a group.
   */
  countGroupLiberties(board: GoBoard, group: GoPosition[], boardSize: number): number {
    const libertySet = new Set<string>();

    for (const pos of group) {
      for (const [dr, dc] of DIRECTIONS) {
        const nr = pos.row + dr;
        const nc = pos.col + dc;
        if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize) {
          if (board[nr][nc] === null) {
            libertySet.add(`${nr},${nc}`);
          }
        }
      }
    }

    return libertySet.size;
  }

  /**
   * Get group and its liberties for a position on the current board.
   */
  getGroupLiberties(row: number, col: number): { group: GoPosition[]; liberties: number } {
    const { board, boardSize } = this.state;
    if (board[row][col] === null) return { group: [], liberties: 0 };
    const group = this.getGroup(board, row, col, boardSize);
    const liberties = this.countGroupLiberties(board, group, boardSize);
    return { group, liberties };
  }

  // =========================================================================
  // Game Over & Scoring
  // =========================================================================

  /**
   * Check if the game is over.
   */
  isGameOver(): boolean {
    return this.state.gameStatus === 'completed';
  }

  /**
   * Calculate score using Chinese rules (area scoring).
   * Score = stones on board + empty territory surrounded by your stones.
   * White gets komi.
   */
  calculateScore(): GoScore {
    const { board, boardSize, komi } = this.state;

    let blackStones = 0;
    let whiteStones = 0;

    // Count stones
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (board[r][c] === 'black') blackStones++;
        else if (board[r][c] === 'white') whiteStones++;
      }
    }

    // Calculate territory
    const territory = this.calculateTerritory();
    let blackTerritory = 0;
    let whiteTerritory = 0;

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (territory[r][c] === 'black') blackTerritory++;
        else if (territory[r][c] === 'white') whiteTerritory++;
      }
    }

    return {
      black: blackStones + blackTerritory,
      white: whiteStones + whiteTerritory + komi,
      blackTerritory,
      whiteTerritory,
      blackStones,
      whiteStones,
      komi,
    };
  }

  /**
   * Calculate territory map: for each empty point, determine which player
   * (if any) surrounds it. Uses flood-fill on empty regions.
   */
  calculateTerritory(): GoTerritoryMap {
    const { board, boardSize } = this.state;
    const territory: GoTerritoryMap = Array.from({ length: boardSize }, () =>
      Array.from({ length: boardSize }, () => null)
    );

    const visited = new Set<string>();

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (board[r][c] !== null) continue;
        const key = `${r},${c}`;
        if (visited.has(key)) continue;

        // Flood-fill to find the empty region
        const region: GoPosition[] = [];
        const regionVisited = new Set<string>();
        const stack: GoPosition[] = [{ row: r, col: c }];
        let touchesBlack = false;
        let touchesWhite = false;

        while (stack.length > 0) {
          const pos = stack.pop()!;
          const pk = `${pos.row},${pos.col}`;
          if (regionVisited.has(pk)) continue;
          regionVisited.add(pk);

          if (
            pos.row < 0 || pos.row >= boardSize ||
            pos.col < 0 || pos.col >= boardSize
          ) continue;

          const cell = board[pos.row][pos.col];
          if (cell === 'black') {
            touchesBlack = true;
            continue;
          }
          if (cell === 'white') {
            touchesWhite = true;
            continue;
          }

          // Empty cell — part of the region
          region.push(pos);
          visited.add(pk);

          for (const [dr, dc] of DIRECTIONS) {
            stack.push({ row: pos.row + dr, col: pos.col + dc });
          }
        }

        // Assign territory
        let owner: GoColor | null = null;
        if (touchesBlack && !touchesWhite) owner = 'black';
        else if (touchesWhite && !touchesBlack) owner = 'white';

        for (const pos of region) {
          territory[pos.row][pos.col] = owner;
        }
      }
    }

    return territory;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getBoard(): GoBoard {
    return this.cloneBoard(this.state.board);
  }

  getCurrentPlayer(): GoColor {
    return this.state.currentPlayer;
  }

  getBoardSize(): GoBoardSize {
    return this.state.boardSize;
  }

  getCaptures(): { black: number; white: number } {
    return { ...this.state.captures };
  }

  getKoPoint(): GoPosition | null {
    return this.state.koPoint ? { ...this.state.koPoint } : null;
  }

  getMoveHistory(): GoMoveRecord[] {
    return [...this.state.moveHistory];
  }

  getGameState(): GoGameState {
    return {
      ...this.state,
      board: this.cloneBoard(this.state.board),
      captures: { ...this.state.captures },
      moveHistory: [...this.state.moveHistory],
      koPoint: this.state.koPoint ? { ...this.state.koPoint } : null,
      score: this.state.score ? { ...this.state.score } : undefined,
      territory: this.state.territory
        ? this.state.territory.map(row => [...row])
        : undefined,
    };
  }

  getWinner(): GoColor | 'draw' | undefined {
    return this.state.winner;
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(json: string): GoGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);

      if (!parsed.board || !Array.isArray(parsed.board)) {
        return { error: 'Invalid state: missing board' };
      }
      if (!['black', 'white'].includes(parsed.currentPlayer)) {
        return { error: 'Invalid state: invalid currentPlayer' };
      }
      if (!['active', 'completed'].includes(parsed.gameStatus)) {
        return { error: 'Invalid state: invalid gameStatus' };
      }

      return parsed as GoGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  setGameState(state: GoGameState): void {
    this.state = {
      ...state,
      board: this.cloneBoard(state.board),
      captures: { ...state.captures },
      moveHistory: [...state.moveHistory],
      koPoint: state.koPoint ? { ...state.koPoint } : null,
      score: state.score ? { ...state.score } : undefined,
      territory: state.territory
        ? state.territory.map(row => [...row])
        : undefined,
    };
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private createEmptyBoard(size: number): GoBoard {
    return Array.from({ length: size }, () =>
      Array.from({ length: size }, () => null as GoCell)
    );
  }

  private createEmptyState(size: GoBoardSize): GoGameState {
    return {
      board: this.createEmptyBoard(size),
      boardSize: size,
      currentPlayer: 'black',
      captures: { black: 0, white: 0 },
      moveHistory: [],
      koPoint: null,
      consecutivePasses: 0,
      gameStatus: 'active',
      komi: DEFAULT_KOMI,
      gameId: '',
      startedAt: 0,
    };
  }

  private cloneBoard(board: GoBoard): GoBoard {
    return board.map(row => [...row]);
  }

  private getOpponent(player: GoColor): GoColor {
    return player === 'black' ? 'white' : 'black';
  }

  /**
   * End the game: calculate score and determine winner.
   */
  private endGame(): void {
    const score = this.calculateScore();
    const territory = this.calculateTerritory();

    this.state.gameStatus = 'completed';
    this.state.score = score;
    this.state.territory = territory;

    if (score.black > score.white) {
      this.state.winner = 'black';
    } else if (score.white > score.black) {
      this.state.winner = 'white';
    } else {
      this.state.winner = 'draw';
    }
  }
}
